import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { formatNyDateTime } from '../../utils/datetime';

const NotificationBell = ({ language = 'pt-BR', onNotificationClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);  // Ref para detectar click fora

  const loadNotifications = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      console.log('🔔 NotificationBell: Carregando notificações...');
      const response = await axios.get(`${API_URL}/api/notifications/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('🔔 NotificationBell: Recebidas', response.data.length, 'notificações');
      setNotifications(response.data);
      setUnreadCount(response.data.length);
    } catch (error) {
      console.error('🔔 NotificationBell: Erro ao carregar notificações:', error);
    }
  };

  useEffect(() => {
    console.log('🔔 NotificationBell montado!');
    loadNotifications();
    // Atualizar a cada 30 segundos
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const markAsRead = async (notificationId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      await axios.put(
        `${API_URL}/api/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    // Some da lista imediatamente
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      await axios.delete(
        `${API_URL}/api/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(language === 'pt-BR' ? 'Notificação removida' : 'Notification removed');
    } catch (error) {
      console.error('Erro ao deletar:', error);
      loadNotifications(); // reverte em caso de erro
    }
  };

  const handleNotificationClick = (notification) => {
    // 1) Abre o modal/ação primeiro (se houver)
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    // 2) Some da lista na hora (como se já tivesse visualizado)
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    setUnreadCount((c) => Math.max(0, c - 1));
    // 3) Persiste no backend (sem recarregar, p/ não repor da fila)
    markAsRead(notification.id);
  };

  const getPriorityColor = (prioridade) => {
    const priority = prioridade || 'normal';
    switch (priority) {
      case 'urgent':
      case 'urgente':
        return 'text-red-600';
      case 'high':
      case 'alta':
        return 'text-orange-600';
      case 'medium':
      case 'media':
        return 'text-blue-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setShowDropdown(!showDropdown)}
        data-testid="notif-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {showDropdown && (
        <div 
          className="fixed sm:absolute left-0 right-0 sm:right-0 sm:left-auto top-16 sm:top-auto sm:mt-2 w-full sm:w-96 max-w-[calc(100vw-1rem)] sm:max-w-96 bg-white rounded-lg shadow-lg border z-50 max-h-[70vh] sm:max-h-96 overflow-y-auto px-2 sm:px-0"
          data-testid="notif-dropdown"
        >
          <div className="p-3 border-b flex items-center justify-between">
            <h3 className="font-semibold">
              {language === 'pt-BR' ? 'Notificações' : 'Notifications'}
            </h3>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    // Some tudo imediatamente
                    setNotifications([]);
                    setUnreadCount(0);
                    try {
                      const token = sessionStorage.getItem('access_token');
                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                      
                      await axios.post(
                        `${API_URL}/api/notifications/mark-all-read`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      toast.success(language === 'pt-BR' ? '✅ Todas marcadas como lidas' : '✅ All marked as read');
                    } catch (error) {
                      console.error('Erro:', error);
                      toast.error(language === 'pt-BR' ? 'Erro ao marcar' : 'Error marking');
                      loadNotifications(); // reverte em caso de erro
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700"
                  data-testid="notif-mark-all-btn"
                >
                  ✓ {language === 'pt-BR' ? 'Marcar todas' : 'Mark all'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDropdown(false)}
              >
                ✕
              </Button>
            </div>
          </div>

          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>{language === 'pt-BR' ? 'Nenhuma notificação' : 'No notifications'}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleNotificationClick(notif)}
                  data-testid={`notif-item-${notif.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${getPriorityColor(notif.prioridade || notif.priority)}`}>
                        {notif.titulo || notif.title || 'Notificação'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {notif.mensagem || notif.message || ''}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatNyDateTime(notif.created_at)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notif.id);
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;